import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  XCircle,
  X,
  RefreshCw,
  CreditCard
} from 'lucide-react';
import { api } from '../services/api';

interface Payment {
  id: number;
  status: string;
  amount: number;
  description: string;
  created_at: string;
  subscription_id?: number;
  mercadopago_payment_id?: string | null;
}

interface PaymentNotificationsProps {
  autoCheck?: boolean;
  checkInterval?: number;
}

export const PaymentNotifications: React.FC<PaymentNotificationsProps> = ({
  autoCheck = true,
  checkInterval = 60000 // 1 minuto
}) => {
  const navigate = useNavigate();
  const [failedPayments, setFailedPayments] = useState<Payment[]>([]);
  const [pendingPayments, setPendingPayments] = useState<Payment[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [retrying, setRetrying] = useState<number | null>(null);

  const checkPayments = useCallback(async () => {
    try {
      // Verificar se há token de autenticação
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await api.get('/payments/payments');

      if (response.data.success && response.data.data) {
        const payments = response.data.data;

        // Filtrar pagamentos com problemas
        const failed = payments.filter((p: Payment) =>
          ['rejected', 'cancelled'].includes(p.status) &&
          !dismissed.has(p.id)
        );

        const pending = payments.filter((p: Payment) =>
          p.status === 'pending' &&
          !!p.mercadopago_payment_id &&
          !dismissed.has(p.id)
        );

        setFailedPayments(failed.slice(0, 3)); // Máximo 3 notificações
        setPendingPayments(pending.slice(0, 2)); // Máximo 2 notificações
      }
    } catch (error: any) {
      // Silencioso - não logar erros 404 ou 401 (usuário sem pagamentos/não autenticado)
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        console.error('Erro ao verificar pagamentos:', error);
      }
    }
  }, [dismissed]);

  useEffect(() => {
    if (autoCheck) {
      checkPayments();
      const interval = setInterval(checkPayments, checkInterval);
      return () => clearInterval(interval);
    }
  }, [autoCheck, checkInterval, checkPayments]);

  const handleDismiss = (paymentId: number) => {
    setDismissed(prev => new Set(prev).add(paymentId));
    setFailedPayments(prev => prev.filter(p => p.id !== paymentId));
    setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  const handleRetry = async (paymentId: number) => {
    try {
      setRetrying(paymentId);
      const response = await api.post(`/payments/payment/${paymentId}/retry`);

      if (response.data.success) {
        handleDismiss(paymentId);
        // Recarregar após 2 segundos para ver o novo status
        setTimeout(checkPayments, 2000);
      }
    } catch (error) {
      console.error('Erro ao reprocessar pagamento:', error);
    } finally {
      setRetrying(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min atrás`;
    } else if (diffHours < 24) {
      return `${diffHours}h atrás`;
    } else if (diffDays < 7) {
      return `${diffDays}d atrás`;
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short'
      });
    }
  };

  if (failedPayments.length === 0 && pendingPayments.length === 0) {
    return null;
  }

  return (
    <Container>
      {failedPayments.map(payment => (
        <Notification key={payment.id} type="error">
          <NotificationIcon type="error">
            <XCircle size={20} />
          </NotificationIcon>

          <NotificationContent>
            <NotificationTitle>Pagamento Falhou</NotificationTitle>
            <NotificationMessage>
              {payment.description || 'Pagamento de assinatura'}
            </NotificationMessage>
            <NotificationMeta>
              {formatCurrency(payment.amount)} • {formatDate(payment.created_at)}
            </NotificationMeta>
          </NotificationContent>

          <NotificationActions>
            <RetryButton
              onClick={() => handleRetry(payment.id)}
              disabled={retrying === payment.id}
            >
              {retrying === payment.id ? (
                <RefreshCw size={16} className="spin" />
              ) : (
                <>
                  <RefreshCw size={16} />
                  Tentar Novamente
                </>
              )}
            </RetryButton>
            <UpdateCardButton onClick={() => navigate('/billing/cards')}>
              <CreditCard size={16} />
              Atualizar Cartão
            </UpdateCardButton>
            <CloseButton onClick={() => handleDismiss(payment.id)}>
              <X size={16} />
            </CloseButton>
          </NotificationActions>
        </Notification>
      ))}

      {pendingPayments.map(payment => (
        <Notification key={payment.id} type="warning">
          <NotificationIcon type="warning">
            <AlertCircle size={20} />
          </NotificationIcon>

          <NotificationContent>
            <NotificationTitle>Pagamento Pendente</NotificationTitle>
            <NotificationMessage>
              {payment.description || 'Pagamento de assinatura'} está aguardando confirmação.
            </NotificationMessage>
            <NotificationMeta>
              {formatCurrency(payment.amount)} • {formatDate(payment.created_at)}
            </NotificationMeta>
          </NotificationContent>

          <NotificationActions>
            <ViewButton onClick={() => navigate('/billing')}>
              Ver Detalhes
            </ViewButton>
            <CloseButton onClick={() => handleDismiss(payment.id)}>
              <X size={16} />
            </CloseButton>
          </NotificationActions>
        </Notification>
      ))}
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 450px;
  width: calc(100% - 40px);

  @media (max-width: 768px) {
    top: 70px;
    right: 10px;
    left: 10px;
    max-width: none;
    width: auto;
  }
`;

const Notification = styled.div<{ type: 'error' | 'warning' }>`
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: ${props => props.type === 'error'
    ? 'rgba(255, 71, 87, 0.1)'
    : 'rgba(255, 165, 0, 0.1)'};
  border: 1px solid ${props => props.type === 'error'
    ? 'rgba(255, 71, 87, 0.3)'
    : 'rgba(255, 165, 0, 0.3)'};
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationIcon = styled.div<{ type: 'error' | 'warning' }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.type === 'error'
    ? 'rgba(255, 71, 87, 0.2)'
    : 'rgba(255, 165, 0, 0.2)'};
  color: ${props => props.type === 'error' ? '#ff4757' : '#ffa500'};
  border-radius: 8px;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.3rem;
`;

const NotificationMessage = styled.div`
  font-size: 0.9rem;
  color: #ccc;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const NotificationMeta = styled.div`
  font-size: 0.8rem;
  color: #999;
`;

const NotificationActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const BaseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const RetryButton = styled(BaseButton)`
  background: rgba(0, 212, 170, 0.2);
  border: 1px solid #00d4aa;
  color: #00d4aa;

  &:hover:not(:disabled) {
    background: rgba(0, 212, 170, 0.3);
  }
`;

const UpdateCardButton = styled(BaseButton)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ViewButton = styled(BaseButton)`
  background: rgba(255, 165, 0, 0.2);
  border: 1px solid #ffa500;
  color: #ffa500;

  &:hover:not(:disabled) {
    background: rgba(255, 165, 0, 0.3);
  }
`;

const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #999;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }
`;
